import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { isSignInRefusal, SIGN_IN_REFUSAL_SENTENCE, type AuthMode } from '@pb/shared';
import { ApiError } from '../api/client';
import { getAuthMode, getMe, login } from '../api/auth';
import { Loading } from '../components/States';
import { useSession } from '../lib/session';

function safeNext(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/login')) return '/';
  return value;
}

function centralSignInUrl(next: string): string {
  return `/api/auth/login?next=${encodeURIComponent(next)}`;
}

function LoginCard({ children }: { children: ReactNode }) {
  return (
    <div className="login-page">
      <main className="card login-card">
        <div className="brand">
          <img src="/favicon.svg" alt="" width={28} height={28} />
          PresentationBuilder
        </div>
        {children}
      </main>
    </div>
  );
}

export function LoginPage() {
  const { me } = useSession();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<AuthMode | null>(null);
  const next = safeNext(params.get('next'));
  const error = params.get('error');
  const signedOut = params.has('signed_out');
  const forward = mode === 'central' && !me && error === null && !signedOut;

  useEffect(() => {
    let cancelled = false;
    getAuthMode()
      .then((dto) => {
        if (!cancelled) setMode(dto.mode);
      })
      .catch(() => {
        if (!cancelled) setMode('password');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (forward) window.location.assign(centralSignInUrl(next));
  }, [forward, next]);

  if (me) return <Navigate to={next} replace />;

  if (mode === null || forward) {
    return (
      <LoginCard>
        <Loading />
      </LoginCard>
    );
  }

  if (mode === 'central') {
    const signIn = () => window.location.assign(centralSignInUrl(next));
    if (error !== null) {
      return (
        <LoginCard>
          <h1 style={{ fontSize: 'var(--text-xl)' }}>Sign in</h1>
          <div className="stack">
            <div className="alert alert-error" role="alert">
              {SIGN_IN_REFUSAL_SENTENCE[isSignInRefusal(error) ? error : 'sign_in_failed']}
            </div>
            <button type="button" className="btn btn-primary" onClick={signIn}>
              Sign in again
            </button>
          </div>
        </LoginCard>
      );
    }
    return (
      <LoginCard>
        <h1 style={{ fontSize: 'var(--text-xl)' }}>You are signed out.</h1>
        <div className="stack">
          <button type="button" className="btn btn-primary" onClick={signIn}>
            Sign in
          </button>
        </div>
      </LoginCard>
    );
  }

  return <PasswordLogin next={next} />;
}

function PasswordLogin({ next }: { next: string }) {
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      const current = await getMe();
      setMe(current);
      navigate(next, { replace: true });
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        setError('The email or password is not correct.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError(err.message || 'Too many sign-in attempts. Wait a moment and try again.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Signing in failed. Try again.');
      }
    }
  };

  return (
    <LoginCard>
      <h1 style={{ fontSize: 'var(--text-xl)' }}>Sign in</h1>
      <form className="stack" onSubmit={(event) => void submit(event)}>
        <div className="field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            className="input"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        {error ? (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </LoginCard>
  );
}
